/**
 * Remote Logger to send browser logs to the terminal
 */
export class RemoteLogger {
  private static LOG_URL = "/api/log";

  private static async send(level: string, message: string, data?: any) {
    try {
      // Handle Error objects specifically as they don't stringify well
      let processedData = data;
      if (data instanceof Error) {
        processedData = {
          name: data.name,
          message: data.message,
          stack: data.stack,
        };
      }

      // Safeguard: Stringify and check size to avoid PayloadTooLarge errors
      let bodyString = JSON.stringify({ level, message, data: processedData });
      
      // Strict limit: 10KB to be absolutely safe
      if (bodyString.length > 10000) {
        let truncatedData = "{ Large Data Hidden }";
        
        if (Array.isArray(processedData)) {
          truncatedData = processedData.slice(0, 1).concat([{ _note: "... list truncated to 1 item" }]);
        } else if (processedData && typeof processedData === "object") {
          if (Array.isArray(processedData.data)) {
            truncatedData = { 
              ...processedData, 
              data: processedData.data.slice(0, 1).concat([{ _note: "... nested list truncated to 1 item" }]) 
            };
          } else {
            // If still too large after basic truncation, just strip the data
            truncatedData = { _note: "Data too large for terminal log" };
          }
        }
        
        bodyString = JSON.stringify({ 
          level, 
          message: message + " (Aggressively Truncated)", 
          data: truncatedData 
        });
        
        // Final check: if it's STILL too big, send nothing but the message
        if (bodyString.length > 10000) {
          bodyString = JSON.stringify({ level, message: message + " (Data Removed - Too Large)" });
        }
      }

      await fetch(this.LOG_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyString,
      });
    } catch (error) {
      // Fail silently
    }
  }

  static info(message: string, data?: any) {
    console.log(`[INFO] ${message}`, data || "");
    this.send("info", message, data);
  }

  static warn(message: string, data?: any) {
    console.warn(`[WARN] ${message}`, data || "");
    this.send("warn", message, data);
  }

  static error(message: string, data?: any) {
    console.error(`[ERROR] ${message}`, data || "");
    this.send("error", message, data);
  }
}
